import React from 'react';
import {useAppState, useMobileNavController, useRootStore, useUserState} from "@stores/AppStateContext";
import {useCrudServices} from "../../../../platform/hooks/useServiceCombinations";


import {observer} from "mobx-react-lite";


import {AddClusterIcon} from "../../../components/icons/AddClusterIcon";
import {AddIcon} from "../../../components/icons/AddIcon";
import {CloseIcon} from "../../../components/icons/CloseIcon";
import {CommentsIcon} from "../../../components/icons/CommentsIcon";
import {GroupIcon} from "../../../components/icons/GroupIcon";
import {HistoryIcon} from "../../../components/icons/HistoryIcon";
import {MarkAsReadIcon} from "../../../components/icons/MarkAsReadIcon";
import {MenuIcon} from "../../../components/icons/MenuIcon";
import {MoreHorizontalIcon} from "../../../components/icons/MoreHorizontalIcon";
import {OutlineIcon} from "../../../components/icons/OutlineIcon";

import {handleCreateCluster, handleCreateItem} from "../../../../actions/utils/handleCreateItem";
import {getDocPath} from "@stores/ActivityProvider";
import {RightMenuName} from "@stores/RootStore";
import {hasOtherActiveUsers} from "../../../../utils/userHelper";
import {ActiveUserBadge} from "../../RightMenu/elements/ActivUsers/ActiveUsersBadge";
import {getPageTitle} from "../../HeaderMenu";
import {openDocumentContextMenu} from "../../context-menu/DocumentContextMenu/openDocumentContextMenu";


export const EditorHeader = observer(() => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const crudServices = useCrudServices();
    const userState = useUserState();
    const dsManager = userState!.dsManager!;
    const root = rootStore;
    const mobileNav = useMobileNavController();
    const globalState = userState;

    const workspace = root.view.workspace;
    const selectedItem = workspace?.selected ?? null;
    const isTreeCollapsed = root.isTreeCollapsed;
    const isReader = !!globalState?.isCurrentUserReadOnly;
    const isRightMenuOpen = root.rightMenu !== '';

    const toggleRightMenu = (menu: RightMenuName) => {
        const nextMenu: RightMenuName = root.rightMenu !== menu ? menu : '';
        root.setRightMenu(nextMenu);
        if (isRightMenuOpen) {
            root.setRightMenu('');
            mobileNav.openPage();
        } else {
            mobileNav.openRightMenu();
        }
    };

    if (!isTreeCollapsed) {
        const title = getPageTitle(root.view.page);
        return (
            <div className={'MobileHeader MobileHeader--simple'}>
                <MenuIcon
                    size={24}
                    className={'MobileHeader__icon'}
                    onClick={() => {
                        // On KB tree layer, MenuIcon opens MainSidebar overlay on mobile
                        mobileNav.openMainSidebar();
                    }}
                />
                <div>
                    {title}
                </div>
            </div>
        );
    }

    const handleCloseClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        mobileNav.openKbTree();
    };

    if (selectedItem?.isCluster) {
        return (
            <div className={'MobileHeader theme-dark'}>
                <CloseIcon
                    className={'MobileHeader__icon'}
                    size={24}
                    onClick={handleCloseClick}
                />
                {!isReader && (
                    <div className={'MobileHeader__right'}>
                        <AddIcon
                            className={'MobileHeader__icon'}
                            size={24}
                            additionalPadding={12}
                            onClick={handleCreateItem(crudServices, workspace!, appState, selectedItem!)}
                        />
                        <AddClusterIcon
                            className={'MobileHeader__icon'}
                            size={24}
                            additionalPadding={12}
                            onClick={handleCreateCluster(crudServices, workspace!, appState, selectedItem!)}
                        />
                    </div>
                )}
            </div>
        );
    }

    const currentUserId = globalState?.currentUserId ?? null;
    const activityProvider = globalState?.activityProvider;
    const currentDocActivity = workspace && selectedItem && activityProvider
        ? activityProvider.activity.get(getDocPath(workspace.id, selectedItem.id))
        : undefined;
    const showActiveUsers = !!(currentDocActivity && currentUserId && hasOtherActiveUsers(currentDocActivity, currentUserId));

    return (
        <div className={'MobileHeader theme-dark'}>
            <CloseIcon
                className={'MobileHeader__icon'}
                size={24}
                onClick={handleCloseClick}
            />
            <div className={'MobileHeader__right'}>
                <div className="ActiveUserIcon">
                    <GroupIcon
                        className={'MobileHeader__icon'}
                        size={24}
                        additionalPadding={12}
                        onClick={(event) => {
                            event.stopPropagation();
                            toggleRightMenu('activeUsers');
                        }}
                    />
                    <ActiveUserBadge
                        className="ActiveUsersIcon_badge"
                        sx={{
                            '--Badge-minHeight': '8px',
                        }}
                        invisible={!showActiveUsers}
                    />
                </div>
                <HistoryIcon
                    className={'MobileHeader__icon'}
                    size={24}
                    additionalPadding={12}
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleRightMenu('history');
                    }}
                />
                <CommentsIcon
                    className={'MobileHeader__icon'}
                    size={24}
                    additionalPadding={12}
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleRightMenu('comments');
                    }}
                />
                <OutlineIcon
                    className={'MobileHeader__icon'}
                    size={24}
                    additionalPadding={12}
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleRightMenu('outline');
                    }}
                />
                <MarkAsReadIcon
                    className={'MobileHeader__icon'}
                    size={24}
                    additionalPadding={12}
                    onClick={(event) => {
                        event.stopPropagation();
                        toggleRightMenu('acknowledgement');
                    }}
                />
                <MoreHorizontalIcon
                    className={'MobileHeader__icon'}
                    size={24}
                    additionalPadding={12}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!selectedItem) {
                            return;
                        }
                        openDocumentContextMenu(appState, {
                            item: selectedItem,
                            withOverlay: true,
                        });
                    }}
                />
            </div>
        </div>
    );
});
